"use client";
import { Layout } from "@portal/app/_components/ui-elements/Layout";
import { useMixpanel } from "@portal/app/_components/hooks/useMixpanel";
import { CustomerNavBar } from "@portal/app/_components/ui-elements/CustomerNavBar";
import {
  canDismissDreamSearchLocationPrompt,
  DEFAULT_SEARCH_PAGE_SIZE,
  getResultsHeading,
  shouldEditDreamSearchLocationInitially,
} from "@portal/app/health/_components/utils/searchHelpers";
import { DreamSearchProvider } from "@portal/app/health/_components/DreamSearchContext";
import {
  DreamSearchFilterProvider,
  useDreamSearchFilterContext,
} from "@portal/app/health/_components/components/filter/DreamSearchFilterContext";
import { Suspense, useEffect, useMemo, useState } from "react";
import { ZipCodeForm } from "@portal/app/health/_components/components/ZipCodeForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "@mui/material/Button";
import { DreamMuiHandledError } from "@portal/app/_components/ui-elements/Mui/DreamMuiHandledError";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { DialogActionButton } from "@portal/app/_components/ui-elements/ConfirmationDialog";
import { DreamSearchFilterPills } from "@portal/app/health/_components/components/filter/DreamSearchFilterPills";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons/faSpinner";
import { DreamSearchResultSkeletons } from "@portal/app/health/_components/components/DreamSearchResult/DreamSearchSkeletonItem";
import { DreamSearchResults } from "@portal/app/health/_components/components/DreamSearchResult/DreamSearchResults";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography, { TypographyProps } from "@mui/material/Typography";
import { DreamMuiDialog } from "@portal/app/_components/ui-elements/Mui/DreamMuiDialog";
import { useAffiliateLocalStorage } from "@portal/app/_components/hooks/useAffiliateLocalStorage";
import { DreamSearchPageProps } from "@portal/app/health/[zip]/pageHelpers";
import { DreamSearchSubindustryFilter } from "@portal/app/health/_components/components/filter/DreamSearchSubindustryFilter";
import { DreamSearchFormFunnelBanner } from "@portal/app/health/_components/DreamSearchFormFunnelBanner";
import Divider from "@mui/material/Divider";
import { DreamSearchFilter } from "./components/filter/DreamSearchFilter/DreamSearchFilter";
import { LoadingStateFilterModal } from "./components/filter/LoadingStateFilterModal";
import uniqBy from "lodash/uniqBy";
import {
  ButtonSkeleton,
  TypographySkeleton,
} from "@portal/app/_components/ui-elements/Mui/DreamMuiSkeleton";
import { usePageContext } from "@portal/app/_components/context/PageContext";
import { ErrorBoundary } from "@sentry/nextjs";
import {
  DreamSearchForDreamSearchQuery,
  DreamSearchForDreamSearchQueryVariables,
} from "@portal/app/health/_components/generated";
import type { UseSuspenseQueryResult } from "@apollo/client/react";
import { Filters } from "@portal/app/_common/types/dreamSearch";
import { DreamSearchContextProps } from "@portal/app/health/_components/DreamSearchContext";
import { FormFunnelAttributionProvider } from "@portal/app/health/_components/FormFunnelAttributionContext";
import { SchoolSelectionTrackingProvider } from "@portal/app/health/_components/SchoolSelectionTrackingContext";
import { DreamSearchPageHeading } from "@portal/app/health/_components/components/DreamSearchPageHeading";

const DreamSearchResultsView = ({
  items,
  error,
  isLoading,
  otherRecommendedItems,
  hasNextPage,
  fetchMore,
  searchCity,
  searchState,
  searchZip,
  setCity,
  setState,
  setZip,
  setAddressLoading,
  filters,
  setFilters,
  clearAllFilters,
  industry,
  page,
  setPage,
  variables,
  isFiltersModalOpen,
  setMobileFiltersModal,
  filterElements,
}: {
  items: DreamSearchContextProps["items"];
  error: DreamSearchContextProps["error"];
  isLoading: boolean;
  otherRecommendedItems: DreamSearchContextProps["otherRecommendedItems"];
  hasNextPage: boolean;
  fetchMore: DreamSearchContextProps["fetchMore"];
  searchCity: string;
  searchState: string;
  searchZip: string;
  setCity: React.Dispatch<React.SetStateAction<string>>;
  setState: React.Dispatch<React.SetStateAction<string>>;
  setZip: React.Dispatch<React.SetStateAction<string>>;
  setAddressLoading: React.Dispatch<React.SetStateAction<boolean>>;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  clearAllFilters: () => void;
  industry?: GQL.VocationIndustry;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  variables: DreamSearchForDreamSearchQueryVariables;
  isFiltersModalOpen: boolean;
  setMobileFiltersModal: React.Dispatch<React.SetStateAction<boolean>>;
  filterElements: React.ReactNode[];
}) => {
  useEffect(() => {
    setCity(searchCity);
    setState(searchState);
    setZip(searchZip);
    setAddressLoading(false);
  }, [searchCity, searchState, searchZip, setCity, setState, setZip, setAddressLoading]);

  return (
    <>
      {error ? (
        <DreamMuiHandledError />
      ) : (
        <>
          <DreamSearchFilterPills onPillClick={() => setMobileFiltersModal(true)} />
          <ResultsContainer>
            {/*
              The results list needs a persistent <h2> so the page has an outline below the <h1>.
              While loading, this is the visible sticky "Loading your results" bar. Once results are
              in, the bar has no visible counterpart in the design, so the heading stays in the
              accessibility tree / DOM but is hidden from sighted users rather than dropped.
            */}
            <ResultsTitle $visuallyHidden={!isLoading}>
              <ResultsTitleCopy variant="new_h2">
                {isLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    &nbsp; Loading your results
                  </>
                ) : (
                  getResultsHeading({ industry, searchCity, searchState, searchZip })
                )}
              </ResultsTitleCopy>
            </ResultsTitle>
            {isLoading || !items ? (
              <DreamSearchResultSkeletons />
            ) : (
              <>
                <SchoolSelectionTrackingProvider>
                  <DreamSearchResults
                    items={items}
                    otherRecommendedItems={otherRecommendedItems}
                    eventName="Dream Search - Clicked Dreamsearch Card"
                    filters={filters}
                    setFilters={setFilters}
                    clearAllFilters={clearAllFilters}
                    industry={industry}
                    pageSize={DEFAULT_SEARCH_PAGE_SIZE}
                    zip={searchZip}
                  />
                </SchoolSelectionTrackingProvider>
                {hasNextPage && (
                  <SeeMoreButton
                    page={page}
                    setPage={setPage}
                    fetchMore={fetchMore}
                    variables={variables}
                  />
                )}
              </>
            )}
          </ResultsContainer>
        </>
      )}
      <DreamMuiDialogComponent
        open={isFiltersModalOpen}
        onClose={() => setMobileFiltersModal(false)}
        TransitionProps={{ timeout: 0 }}
        BackdropProps={{ timeout: 0 }}
        slotProps={{
          backdrop: {
            timeout: 0,
            style: { transition: "none" },
          },
        }}
      >
        <FiltersTitleAndClearButton />
        <FilterDialogContent>
          <MobileFullDreamSearchRedirectButton>
            <DreamSearchSubindustryFilter />
          </MobileFullDreamSearchRedirectButton>
          <Divider />
          <FilterContainer>{filterElements}</FilterContainer>
        </FilterDialogContent>
        <DialogActions>
          <DialogActionButton
            onClick={() => setMobileFiltersModal(false)}
            data-testid="dreamsearch-mobile-filters-close"
          >
            Done
          </DialogActionButton>
        </DialogActions>
      </DreamMuiDialogComponent>
    </>
  );
};

export const DreamSearchPage = ({
  industry,
  initialUserFilters,
  initialGeoCenter,
  variables,
  showIndustryFilter = false,
  formFunnelAttribution,
  formFunnelVocation,
  pinnedProgramId,
  shouldPromptForLocation = false,
}: DreamSearchPageProps) => {
  const analytics = useMixpanel();
  useAffiliateLocalStorage();
  const [isFiltersModalOpen, setMobileFiltersModal] = useState(false);
  const [isEditingZipCode, setIsEditingZipCode] = useState(
    shouldEditDreamSearchLocationInitially({
      initialZip: initialGeoCenter.zip ?? undefined,
      shouldPromptForLocation,
    })
  );
  const { isMobile } = usePageContext();
  const [city, setCity] = useState(initialGeoCenter.city ?? "");
  const [state, setState] = useState(initialGeoCenter.state ?? "");
  const [zip, setZip] = useState(initialGeoCenter.zip ?? "");
  const [addressLoading, setAddressLoading] = useState(true);
  const canDismissLocationPrompt = canDismissDreamSearchLocationPrompt({
    searchCity: city,
    searchState: state,
    searchZip: zip,
    shouldPromptForLocation,
  });
  void analytics.trackOnePageView("Dream Search - Landing Page", {
    industry,
  });

  const filterElements = useMemo(
    () => filterComponents({ showIndustryFilter }),
    [showIndustryFilter]
  );

  return (
    <FormFunnelAttributionProvider value={formFunnelAttribution}>
      <Layout dreamSearch footer="BOTH" nav={<CustomerNavBar />}>
      <DreamSearchContainer>
        <DreamSearchViewerContainer>
          <ContentContainer>
            <ContentTopSection>
              <HeaderAndDisclaimerContainer>
                <HeaderTextContainer>
                  <DreamSearchPageHeading
                    industry={industry}
                    searchCity={city}
                    searchState={state}
                    searchZip={zip}
                    suppressLocation={isEditingZipCode}
                    isLocationLoading={addressLoading && !(city && state)}
                    onEditLocation={() => setIsEditingZipCode(true)}
                  />
                  {isEditingZipCode && (
                    <ZipCodeContainer>
                      <ZipCodeForm
                        setIsEditingZipCode={setIsEditingZipCode}
                        industry={industry}
                        canCancel={canDismissLocationPrompt}
                        shouldPromptForLocation={shouldPromptForLocation}
                      />
                    </ZipCodeContainer>
                  )}
                </HeaderTextContainer>
              </HeaderAndDisclaimerContainer>
              <FormFunnelBannerContainer data-testid="form-funnel-banner-container">
                <DreamSearchFormFunnelBanner />
              </FormFunnelBannerContainer>
            </ContentTopSection>
            <DreamSearchFilterProvider
              allowQueryZipOverride
              industry={industry}
              vocation={formFunnelVocation}
              onlyIncludePartnered={!!formFunnelAttribution}
              pinnedProgramId={pinnedProgramId}
              shouldPromptForLocation={shouldPromptForLocation}
              initialGeoCenter={initialGeoCenter}
              initialUserFilters={initialUserFilters}
              complianceExclusionTags={variables.complianceExclusionTags}
              excludedProgramIds={variables.dreamSearchFilters?.program_exclude_id_in}
            >
              {({ filters, setFilters, variables, clearAllFilters, page, setPage }) => (
                <>
                  <Suspense
                    fallback={
                      <>
                        <SuspenseFallbackResults isMobile={isMobile} />
                        <LoadingStateFilterModal
                          isOpen={isFiltersModalOpen}
                          onClose={() => setMobileFiltersModal(false)}
                          filters={filters}
                          clearAllFilters={clearAllFilters}
                        />
                      </>
                    }
                  >
                    <ErrorBoundary fallback={<DreamMuiHandledError />}>
                      <DreamSearchProvider
                        variables={variables}
                        industry={industry}
                        filters={filters}
                        setFilters={setFilters}
                      >
                        {({
                          items,
                          error,
                          isLoading,
                          otherRecommendedItems,
                          hasNextPage,
                          fetchMore,
                          city: searchCity,
                          state: searchState,
                          zip: searchZip,
                        }) => (
                          <DreamSearchResultsView
                            items={items}
                            error={error}
                            isLoading={isLoading}
                            otherRecommendedItems={otherRecommendedItems}
                            hasNextPage={hasNextPage}
                            fetchMore={fetchMore}
                            searchCity={searchCity}
                            searchState={searchState}
                            searchZip={searchZip}
                            setCity={setCity}
                            setState={setState}
                            setZip={setZip}
                            setAddressLoading={setAddressLoading}
                            filters={filters}
                            setFilters={setFilters}
                            clearAllFilters={clearAllFilters}
                            industry={industry}
                            page={page}
                            setPage={setPage}
                            variables={variables}
                            isFiltersModalOpen={isFiltersModalOpen}
                            setMobileFiltersModal={setMobileFiltersModal}
                            filterElements={filterElements}
                          />
                        )}
                      </DreamSearchProvider>
                    </ErrorBoundary>
                  </Suspense>
                </>
              )}
            </DreamSearchFilterProvider>
          </ContentContainer>
        </DreamSearchViewerContainer>
      </DreamSearchContainer>
      </Layout>
    </FormFunnelAttributionProvider>
  );
};

const filterComponents = ({ showIndustryFilter }: { showIndustryFilter?: boolean }) => [
  ...(showIndustryFilter
    ? [<DreamSearchFilter filterName="program_vocation_industry" key="program_vocation_industry" />]
    : []),
  <DreamSearchFilter filterName="program_environment" key="program_environment" />,
  <DreamSearchFilter filterName="program_vocation_in" key="program_vocation_in" />,
  <DreamSearchFilter
    filterName="program_payment_options_type_in"
    key="program_payment_options_type_in"
  />,
  <DreamSearchFilter filterName="program_duration_days_lte" key="program_duration_days_lte" />,
  <DreamSearchFilter filterName="program_level_in" key="program_level_in" />,
  <DreamSearchFilter filterName="program_tags_in" key="program_tags_in" />,
];

export const FiltersTitleAndClearButton = () => {
  const { filters, clearAllFilters } = useDreamSearchFilterContext();
  return (
    <FilterTitle>
      {/* Keeps the `page_title` look but renders an h2 — this labels the filter panel, not the
          page, and `page_title` maps to h1. Also rendered on /view via `SEOMetroPage`. */}
      <FiltersTitleText variant="page_title" component="h2">
        Filters
      </FiltersTitleText>
      <Button
        variant="text"
        size="small"
        color="primary"
        onClick={() => clearAllFilters()}
        disabled={!Object.keys(filters).length}
        data-testid="clear-all-filters"
      >
        Clear all
      </Button>
    </FilterTitle>
  );
};

const SuspenseFallbackResults = ({ isMobile }: { isMobile: boolean }) => (
  <>
    <SuspenseFallbackContainer>
      <SuspenseFallbackPillsContainer>
        {Array.from({ length: isMobile ? 2 : 6 }).map((_, index) => (
          <ButtonSkeleton sx={{ height: "60px", width: "110px" }} key={index} />
        ))}
        <ButtonSkeleton sx={{ width: "80px" }} />
      </SuspenseFallbackPillsContainer>
      <TypographySkeleton sx={{ width: "60%" }} />
      <ResultsContainer>
        <DreamSearchResultSkeletons itemCount={6} />
      </ResultsContainer>
    </SuspenseFallbackContainer>
  </>
);

const SeeMoreButton = ({
  page,
  setPage,
  fetchMore,
  variables,
}: {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  fetchMore: UseSuspenseQueryResult<
    DreamSearchForDreamSearchQuery,
    DreamSearchForDreamSearchQueryVariables
  >["fetchMore"];
  variables: DreamSearchForDreamSearchQueryVariables;
}) => (
  <SeeMoreContainer>
    <Button
      variant="outlined"
      data-testid="see-more-programs"
      onClick={() => {
        setPage((currentPage) => currentPage + 1);
        return fetchMore({
          variables: {
            after: (page + 1) * variables.first - 1,
          },
          updateQuery: (
            prev: DreamSearchForDreamSearchQuery,
            { fetchMoreResult }: { fetchMoreResult: DreamSearchForDreamSearchQuery | undefined }
          ) => {
            if (!fetchMoreResult) {
              return prev;
            }
            return {
              ...fetchMoreResult,
              dreamSearchItemsConnection: {
                ...fetchMoreResult.dreamSearchItemsConnection,
                otherRecommendedItems: uniqBy(
                  [
                    ...(prev?.dreamSearchItemsConnection.otherRecommendedItems ?? []),
                    ...(fetchMoreResult.dreamSearchItemsConnection.otherRecommendedItems ?? []),
                  ],
                  "id"
                ),
                edges: uniqBy(
                  [
                    ...(prev?.dreamSearchItemsConnection.edges ?? []),
                    ...fetchMoreResult.dreamSearchItemsConnection.edges,
                  ],
                  "cursor"
                ),
              },
            };
          },
        });
      }}
    >
      See more
    </Button>
  </SeeMoreContainer>
);

const DreamSearchContainer = styled(Box)`
  display: flex;
  flex-direction: column;

  width: 100%;

  ${({ theme }) => theme.breakpoints.down("md")} {
    flex: 1;
    flex-direction: column;
    align-items: stretch;
  }
`;

export const DreamSearchViewerContainer = styled(Box)`
  display: flex;
  flex: 1;
`;

export const ContentTopSection = styled(Box)`
  display: flex;
  flex-direction: column;
`;

export const HeaderTextContainer = styled("div")`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 0.5rem;
  }
`;

export const HeaderAndDisclaimerContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

export const ZipCodeContainer = styled(Box)`
  display: flex;

  .fa-circle-info {
    margin-left: 0.5rem;
    margin-top: 0.2rem;
  }
`;

export const DesktopFiltersStickyContainer = styled(Box)``;

export const DesktopFiltersScrollableArea = styled(Box)`
  padding: 2rem 2rem 6rem;
  overflow-y: auto;
`;

export const ContentContainer = styled(Box)`
  position: relative;
  min-width: 0;

  ${({ theme }) => theme.breakpoints.down("md")} {
    flex: 1 1;
    padding: 1rem;

    display: flex;
    flex-direction: column;
    overflow-x: scroll;

    // On desktop, this hides the horizontal column space taken up by the
    // always-visible scrollbar.

    &::-webkit-scrollbar {
      display: none;
    }
  }

  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-grow: 1;
    padding: 2rem;
  }
`;

const FormFunnelBannerContainer = styled(Box)`
  container-type: inline-size;

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding-top: 1rem;
  }

  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 2rem;
  }
`;

export const ResultsContainer = styled(Box)`
  min-width: 0;

  ${({ theme }) => theme.breakpoints.down("md")} {
    flex: 0 1 0;
    width: 100%;
    z-index: 2;

    background: white;
    margin-top: 0;
    border-radius: unset;
  }
`;

export const FilterDialogContent = styled(DialogContent)`
  scrollbar-width: none;
  // Hide scrollbar on Safari

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const ResultsTitle = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$visuallyHidden",
})<{ $visuallyHidden?: boolean }>`
  position: sticky;
  top: 0;
  padding: 1rem 0;
  background: white;
  display: flex;
  align-items: center;
  // Fixes layer issue with More info button
  z-index: 1;
  // Prevents jank when flashing the loading message. Based off of height of "Back to list" button
  min-height: 40px;

  // Keeps the results heading in the DOM and the accessibility tree once results have loaded,
  // where the design has no visible heading bar. Takes no layout space.
  ${({ $visuallyHidden }) =>
    $visuallyHidden
      ? `
    position: absolute;
    width: 1px;
    height: 1px;
    min-height: 0;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  `
      : ""}
`;

export const ResultsTitleCopy = styled(Typography)`
  margin: 0 1rem 0 0;

  ${({ theme }) => theme.breakpoints.down("md")} {
    flex: 1;
  }
`;

export const ResultsFooterCopy = styled(Typography)`
  text-align: center;
`;

export const DreamSearchTitle = styled(Typography)`
  margin-bottom: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;

  ${({ theme }) => theme.breakpoints.down("md")} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

// Shared by the legacy SEO metro page. DreamSearch itself uses the consolidated
// `DreamSearchPageHeading` component above.
export const HeaderText = styled(Typography)<TypographyProps>`
  margin: 0;
  text-transform: uppercase;
`;

export const FiltersTitleText = styled(Typography)<TypographyProps>`
  margin: 0;
`;

// Shared by the legacy SEO metro page. Its location control has separate copy
// and styling requirements from the DreamSearch page-level heading.
export const ClickableZipCode = styled(Typography)<TypographyProps>`
  display: inline-flex;

  color: ${({ theme }) => theme.palette.primary.main};
  text-decoration: underline;

  gap: 0.25rem;
  align-items: center;
  vertical-align: bottom;

  margin-bottom: 0;

  &:hover {
    cursor: pointer;
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    margin: 0;
  }
`;

export const FilterTitle = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;
  padding: 0;

  ${({ theme }) =>
    theme.breakpoints.up("md") &&
    `
      padding: 2rem;
      margin-bottom: 0;
  `}
`;

export const FilterAccordionDecorator = styled(Box)`
  .MuiAccordion-root {
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;

    &::before {
      display: none;
    }

    .MuiAccordionSummary-root {
      border-bottom: none;
      min-height: fit-content;
      padding: 0;

      .MuiAccordionSummary-content {
        margin: 0;
      }
    }

    .MuiAccordionDetails-root {
      padding: 0;
      margin-top: 0.5rem;

      .MuiCheckbox-root {
        padding: 0.35rem 0.7rem;
      }
    }
  }
`;

export const FilterContainer = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  margin-top: 1.5rem;
`;

export const DreamMuiDialogComponent = styled(DreamMuiDialog)`
  .subindustry_filter {
    margin-bottom: 1rem;
  }
`;

export const MobileFullDreamSearchRedirectButton = styled(Box)`
  .full_dream_search_redirect_button {
    height: 5rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`;

export const DisclaimerLink = styled("a")`
  margin-right: 0.25rem;
`;

export const SeeMoreContainer = styled(Box)`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

// Suspense fallback styled components
export const SuspenseFallbackContainer = styled(Box)`
  display: flex;
  gap: 1rem;
  flex-direction: column;
`;

export const SuspenseFallbackPillsContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-top: 1rem;
  padding-bottom: 0;

  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 2rem;
    padding-top: 2rem;
    padding-bottom: 1.5rem;
  }
`;
